import React, { useMemo } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { TableListRow, Typography, TableListActions, DropdownMenuItem, ConfirmationModal, WithInfoIcon } from 'seagull';
import { Download, Void, MoneyReceive, LinkIcon, MailSentLine18, AgreementsListLine } from 'seagull/icons';
import compact from 'lodash/compact';
import { useCopyDirectLink } from "../../hooks/useCopyDirectLink";
import { usePaidExternally } from "../../hooks/usePaidExternally";
import { useOverlayContext, useStore, useStoreActions } from "../../contexts";
import { InvoiceQboIntegrationStatus } from "../InvoiceQboIntegrationStatus";
import { useResendInvoice } from "../../hooks/useResendInvoice";
import { routes } from "../../constants";
import { selectors } from "../../store/state-selectors";
import { Intent } from "../../infra/access-control";
import { useDownloadInvoicePdf } from "../../hooks/invoice/useDownloadInvoicePdf";
import { allowResendAction, allowVoidAction, getPaymentStatusText, getStatusLabel } from "../../business-logic/invoiceLogic";
import styles from "./InvoicesTable.module.scss";
import { InvoicePaymentDisplayStatusType } from 'anchor-scout/types';
import { invoiceModel } from "../../models";
const voidLoader = 'void';
const displayedStatusToSkin = {
  issued: undefined,
  overdue: 'error',
  paymentInitiated: 'success',
  paid: 'success',
  voided: undefined,
  refunded: 'standard',
  refundFailed: 'error',
  disputed: 'error'
};
function getPaymentStatusTooltip(displayedPaymentMethodStatus) {
  if (!displayedPaymentMethodStatus) {
    return '';
  } else {
    switch (displayedPaymentMethodStatus.type) {
      case InvoicePaymentDisplayStatusType.Pending:
        return 'Automatic payment processing will begin shortly';
      case InvoicePaymentDisplayStatusType.Processing:
        return 'Processing the payment may take up to 7 business days';
      case InvoicePaymentDisplayStatusType.Collected:
        return "The payment will be deposited to your bank account within 1-2 business days";
      case InvoicePaymentDisplayStatusType.RefundRequested:
        return 'Upon completion of payment processing, a refund will be issued';
      default:
        return '';
    }
  }
}
function getPaymentStatusSkin(displayedPaymentMethodStatus) {
  if (!displayedPaymentMethodStatus) {
    return undefined;
  } else {
    switch (displayedPaymentMethodStatus.type) {
      case InvoicePaymentDisplayStatusType.PaymentMethodNotSet:
      case InvoicePaymentDisplayStatusType.PaymentFailed:
      case InvoicePaymentDisplayStatusType.StoppedPayment:
        return 'error';
      case InvoicePaymentDisplayStatusType.PaymentMethodUnverified:
      case InvoicePaymentDisplayStatusType.RequiresPaymentApproval:
      case InvoicePaymentDisplayStatusType.RequiresPaymentValidation:
        return 'warning';
      default:
        return undefined;
    }
  }
}
function InvoicesTableRow(props) {
  const {
    invoice,
    hideDueDate,
    showQboIntegration,
    to,
    retry
  } = props;
  const overlayContext = useOverlayContext();
  const {
    markAsPaid,
    shouldShowMarkAsPaid
  } = usePaidExternally();
  const {
    resendInvoice
  } = useResendInvoice();
  const history = useHistory();
  const isVendor = useStore(selectors.user.isVendor);
  const email = useStore(selectors.user.email);
  const testId = "invoice-row";
  const {
    api,
    logBI
  } = useStoreActions();
  const {
    copyDirectLink
  } = useCopyDirectLink();
  const {
    downloadInvoicePdf
  } = useDownloadInvoicePdf();
  const paymentStatusTooltip = getPaymentStatusTooltip(invoice.paymentStatus);
  const paymentStatusText = useMemo(() => {
    return getPaymentStatusText(invoice.paymentStatus, isVendor);
  }, [invoice, isVendor]);
  const onVoidInvoice = () => {
    overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref => {
        let {
          dismissModal
        } = _ref;
        return /*#__PURE__*/React.createElement(ConfirmationModal, {
          title: "Void Invoice",
          content: "Are you sure you want to void invoice ".concat(invoice.formattedNumber, "?"),
          "data-testid": "void-confirmation-modal",
          primaryButtonLabel: "I'm sure",
          secondaryButtonLabel: "Cancel",
          onSecondaryButtonClick: () => dismissModal(),
          onPrimaryButtonClick: async () => {
            try {
              overlayContext.startLoader(voidLoader);
              await api.invoice.void(invoice.id);
              await retry();
              dismissModal(true);
            } catch (error) {
              overlayContext.showErrorToast('Unable to void the invoice, please try again', 'void-invoice-error');
              dismissModal();
            } finally {
              overlayContext.stopLoader(voidLoader);
            }
          }
        });
      }
    });
  };
  return /*#__PURE__*/React.createElement(TableListRow, {
    as: Link,
    to: to,
    key: invoice.id,
    size: "large",
    "data-testid": testId,
    "data-heapid": "invoice",
    items: compact([{
      className: styles.invoiceIdCell,
      value: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
        "data-testid": "".concat(testId, "-invoice-no"),
        skin: "secondary"
      }, invoice.formattedNumber), showQboIntegration && /*#__PURE__*/React.createElement(InvoiceQboIntegrationStatus, {
        testid: "".concat(testId, "-qbo"),
        integration: invoice.integration
      }))
    }, {
      value: /*#__PURE__*/React.createElement(Typography, {
        ellipsis: true,
        weight: "normal",
        "data-testid": "".concat(testId, "-comp-name")
      }, invoiceModel.otherPartyCompanyName(invoice, isVendor), "\xA0")
    }, {
      value: /*#__PURE__*/React.createElement(Typography, {
        skin: displayedStatusToSkin[invoice.displayedStatus],
        weight: "normal",
        "data-testid": "".concat(testId, "-status")
      }, getStatusLabel(invoice.displayedStatus))
    }, {
      value: paymentStatusText && /*#__PURE__*/React.createElement(WithInfoIcon, {
        info: paymentStatusTooltip,
        showInfo: !!paymentStatusTooltip
      }, /*#__PURE__*/React.createElement(Typography, {
        skin: getPaymentStatusSkin(invoice.paymentStatus),
        "data-testid": "".concat(testId, "-payment-status")
      }, paymentStatusText))
    }, {
      value: /*#__PURE__*/React.createElement(Typography, {
        skin: "secondary",
        "data-testid": "".concat(testId, "-issued-on")
      }, invoiceModel.issueDate(invoice))
    }, !hideDueDate && {
      value: /*#__PURE__*/React.createElement(Typography, {
        skin: "secondary",
        "data-testid": "".concat(testId, "-due-date")
      }, invoice.dueDate)
    }, {
      value: /*#__PURE__*/React.createElement(Typography, {
        weight: "light",
        skin: "secondary",
        "data-testid": "".concat(testId, "-amount"),
        ellipsis: true
      }, invoice.totalAmount)
    }]),
    actionColumn: /*#__PURE__*/React.createElement(TableListActions, {
      buttonText: "View",
      onClickButton: () => history.push(to),
      testId: testId,
      btnHeapId: "invoice-view-btn",
      menuHeapId: "invoice-menu-btn"
    }, shouldShowMarkAsPaid(invoiceModel.status(invoice), isVendor) && /*#__PURE__*/React.createElement(DropdownMenuItem, {
      intent: Intent.InvoiceMarkPaidExternally,
      "data-testid": "".concat(testId, "-paid-externally"),
      "data-heapid": "invoice-menu-paid-externally",
      iconStart: /*#__PURE__*/React.createElement(MoneyReceive, null),
      onClick: () => markAsPaid(invoice, retry)
    }, "Mark as paid externally"), allowResendAction(invoice.status, isVendor) && /*#__PURE__*/React.createElement(DropdownMenuItem, {
      intent: Intent.InvoiceResend,
      "data-testid": "".concat(testId, "-resend-invoice"),
      "data-heapid": "invoice-menu-resend-invoice",
      iconStart: /*#__PURE__*/React.createElement(MailSentLine18, {
        width: 24,
        height: 24
      }),
      onClick: () => {
        resendInvoice(invoice).then(retry);
      }
    }, "Resend invoice"), /*#__PURE__*/React.createElement(DropdownMenuItem, {
      intent: Intent.InvoiceDownload,
      "data-testid": "".concat(testId, "-download-invoice"),
      "data-heapid": "invoice-menu-download-invoice",
      iconStart: /*#__PURE__*/React.createElement(Download, null),
      onClick: () => downloadInvoicePdf(invoice.id),
      disabled: !invoice.pdfExists,
      tooltipProps: invoice.pdfExists ? undefined : {
        title: 'Generating PDF'
      }
    }, "Download invoice"), /*#__PURE__*/React.createElement(DropdownMenuItem, {
      "data-testid": "".concat(testId, "-agreement"),
      "data-heapid": "invoice-menu-agreement",
      as: Link,
      to: routes.agreement(invoiceModel.relationshipId(invoice)),
      iconStart: /*#__PURE__*/React.createElement(AgreementsListLine, null)
    }, "View agreement"), allowVoidAction(invoice.status, isVendor) && /*#__PURE__*/React.createElement(DropdownMenuItem, {
      intent: Intent.InvoiceVoid,
      iconStart: /*#__PURE__*/React.createElement(Void, null),
      onClick: onVoidInvoice,
      "data-testid": "".concat(testId, "-void"),
      "data-heapid": "invoice-menu-void"
    }, "Void invoice"), /*#__PURE__*/React.createElement(DropdownMenuItem, {
      intent: Intent.InvoiceCopyLink,
      "data-testid": "".concat(testId, "-copy-link"),
      "data-heapid": "invoice-menu-copy-link",
      iconStart: /*#__PURE__*/React.createElement(LinkIcon, null),
      onClick: () => {
        if (isVendor) {
          logBI(14001, {
            email
          });
        } else {
          logBI(14003);
        }
        copyDirectLink(invoiceModel.relationshipId(invoice), {
          type: 'invoice',
          id: invoice.id
        });
      }
    }, "Copy link"))
  });
}

// Added memoization to combat poor rendering performance in the browser when changing sorting/filtering etc.
export default /*#__PURE__*/React.memo(InvoicesTableRow);